// `extension-alert-candidate-resume` — the "More" view beside a candidate profile, which is an
// iframe over the CV the tenant holds and nothing else.
//
// ⚠️ IT IS SIX LINES OF TEMPLATE AND THE WHOLE SCREEN. There is no viewer, no toolbar and no
// fallback: `viewResume` streams whatever the ATS has, the browser renders it, and a candidate
// with no CV leaves a blank white frame — which is exactly what the reference capture is. A
// port that adds an empty state here invents a screen production does not have.
import { createElement, type ReactElement } from 'react'

export interface CandidateResumeProps {
  candidateId?: number
  /** Included in the URL only when it is a NUMBER; see `resumeUrl`. */
  alertId?: number
  /** `flex-basis` on the element — `ExtensionViewWidth.Detailed` (700) on the More state. */
  flexBasis?: number
}

/**
 * `getViewResumeUrl`.
 *
 * ⚠️ `alertId` IS OMITTED ENTIRELY WHEN IT IS NOT A NUMBER, not sent empty. Upstream builds the
 * query by string concatenation guarded on `angular.isNumber(alertId)`, so the alertless
 * profile asks for `?candidateId=15115` and not `?alertId=&candidateId=15115` — the second is a
 * different request, and the server reads a blank alert id as a parse error rather than as
 * absence.
 */
export function resumeUrl(candidateId: number, alertId?: number): string {
  const alert = alertId === undefined || Number.isNaN(alertId) ? '' : `alertId=${String(alertId)}&`
  return `/chrome-extension-api/alert-candidate/viewResume?${alert}candidateId=${String(candidateId)}`
}

export function CandidateResume({ candidateId, alertId, flexBasis }: CandidateResumeProps): ReactElement {
  const content = (
    <div className="extension-alert-candidate-resume">
      <div className="file-preview">
        {/* `ng-attr-src` — the attribute is not written at all until the expression has a
            value, so the iframe never requests the page's own URL while the id is resolving.
            Rendering `src=""` fetches the current document into the frame. */}
        <iframe
          className="preview-frame"
          title="Resume"
          {...(candidateId === undefined ? {} : { src: resumeUrl(candidateId, alertId) })}
        />
      </div>
    </div>
  )

  // Styled by type, as ever: `extension-alert-candidate-resume { display: flex; flex-direction:
  // column; flex: auto 0 0 }`.
  return createElement(
    'extension-alert-candidate-resume',
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    content,
  )
}
